<template>
  <div class="dp">
    <crm-toolbar
      entity="deal"
      :filters="filters"
      :views="views"
      :view.sync="view"
      :statuses="view === 'board' ? null : statuses"
      @change="setFilters"
    >
      <template #before-views>
        <v-menu offset-y>
          <template #activator="{ on }">
            <button type="button" class="dp__pipe" v-on="on">
              <span class="dp__pipe-dot" :style="{ background: (pipeline && pipeline.color) || '#2166d1' }" />
              <span class="dp__pipe-name">{{ pipeline ? pipeline.title : $t('crmPipeline') }}</span>
              <v-icon small>mdi-chevron-down</v-icon>
            </button>
          </template>
          <v-list dense>
            <v-subheader>{{ $t('crmPipelines') }}</v-subheader>
            <v-list-item v-for="p in pipelines" :key="p.id" :input-value="p.id === pipelineId" @click="pipelineId = p.id">
              <span class="dp__pipe-dot me-2" :style="{ background: p.color || '#2166d1' }" />{{ p.title }}
            </v-list-item>
            <template v-if="canManage">
              <v-divider class="my-1" />
              <v-list-item @click="shell.managePipelines(pipelineId)">
                <v-icon small class="me-2">mdi-filter-cog-outline</v-icon>{{ $t('crmManagePipelines') }}
              </v-list-item>
            </template>
          </v-list>
        </v-menu>
      </template>
      <template #actions>
        <v-btn v-if="canManage" outlined small color="grey darken-1" class="dp__btn" @click="shell.managePipelines(pipelineId)">
          <v-icon small class="me-1">mdi-cog-outline</v-icon>{{ $t('crmPipelineSettings') }}
        </v-btn>
        <v-btn v-if="canExport" outlined small color="grey darken-1" class="dp__btn" :loading="exporting" @click="exportXlsx">
          <v-icon small class="me-1">mdi-microsoft-excel</v-icon>{{ $t('crmExport') }}
        </v-btn>
        <v-btn v-if="canCreate" depressed small color="primary" class="dp__btn" @click="shell.newDeal({ pipelineId })">
          <v-icon small class="me-1">mdi-plus</v-icon>{{ $t('crmNewDeal') }}
          <kbd v-if="!isMobile" class="dp__kbd">D</kbd>
        </v-btn>
      </template>
      <template #summary>{{ $t('crmTotalRecords', { n: num(total) }) }}</template>
    </crm-toolbar>

    <!-- KPI strip -->
    <div class="dp__kpis">
      <div class="dp__kpi">
        <span class="dp__kpi-l">{{ $t('crmOpenDeals') }}</span>
        <b>{{ num(kpi.openCount) }}</b>
        <small v-if="showMoney">{{ money(kpi.openAmount, true) }}</small>
      </div>
      <div v-if="showMoney" class="dp__kpi">
        <span class="dp__kpi-l">{{ $t('crmWeightedPipeline') }}</span>
        <b>{{ money(kpi.weighted, true) || '—' }}</b>
        <small>{{ $t('crmByProbability') }}</small>
      </div>
      <div class="dp__kpi dp__kpi--won">
        <span class="dp__kpi-l">{{ $t('crmWonDeals') }}</span>
        <b>{{ num(kpi.wonCount) }}</b>
        <small v-if="showMoney">{{ money(kpi.wonAmount, true) }}</small>
      </div>
      <div class="dp__kpi dp__kpi--lost">
        <span class="dp__kpi-l">{{ $t('crmLostDeals') }}</span>
        <b>{{ num(kpi.lostCount) }}</b>
        <small>{{ $t('crmWinRate') }}: {{ winRate }}٪</small>
      </div>
      <div class="dp__funnel" :title="$t('crmFunnel')">
        <div v-for="c in openColumns" :key="c.key" class="dp__funnel-seg" :style="{ flex: Math.max(1, c.count || 0), background: c.color || '#2166d1' }">
          <span>{{ c.title }} · {{ num(c.count) }}</span>
        </div>
      </div>
    </div>

    <div class="dp__body">
      <crm-board
        v-if="view === 'board'"
        type="deal"
        group="crm-deals"
        show-amount
        :columns="columns"
        :can-add="canCreate"
        @drop="onDrop"
        @move="(item, target, from) => moveTo(item, from, target)"
        @load-more="loadColumn"
        @add="col => shell.newDeal({ pipelineId, stageId: col.kind === 'open' ? col.key : null })"
        @open="open"
        @log="(it, kind, mode) => shell.logActivity('deal', it, kind, mode)"
      />

      <crm-table
        v-else-if="view === 'table'"
        storage-key="deals"
        :headers="headers"
        :items="rows"
        :total="total"
        :loading="loading"
        :options="tableOptions"
        @update:options="onOptions"
        @open="open"
      >
        <template #bulk="{ ids, clear }">
          <bulk-actions entity="deal" :ids="ids" :pipeline-id="pipelineId" @done="clear(); reload()" />
        </template>
        <template #[`cell.title`]="{ item }">
          <div class="dp__cell-title">
            <b>{{ item.title }}</b>
            <small>{{ customer(item) }}</small>
          </div>
        </template>
        <template #[`cell.amount`]="{ item }">
          <span v-if="item.financial_hidden || item.amount === null" class="dp__muted"><v-icon x-small>mdi-lock-outline</v-icon></span>
          <b v-else>{{ money(item.amount) }}</b>
        </template>
        <template #[`cell.stage`]="{ item }">
          <span class="dp__stage" :style="{ '--sc': stageColor(item.stage_id) }">{{ stageTitle(item) }}</span>
        </template>
        <template #[`cell.status`]="{ item }"><crm-chip :value="item.status" small /></template>
        <template #[`cell.priority`]="{ item }"><crm-chip kind="priority" :value="item.priority" small /></template>
        <template #[`cell.probability`]="{ item }">
          <span class="dp__prob"><i :style="{ width: (item.probability || 0) + '%' }" /></span><small>{{ item.probability || 0 }}٪</small>
        </template>
        <template #[`cell.owner`]="{ item }">
          <span class="dp__owner"><crm-avatar :user-id="item.owner_user_id" :size="22" />{{ memberName(item.owner_user_id) || $t('crmUnassigned') }}</span>
        </template>
        <template #[`cell.close`]="{ item }">
          <span :class="{ 'dp__late': isLate(item) }">{{ fmtDate(item.expected_close_date) || '—' }}</span>
        </template>
        <template #[`cell.next`]="{ item }">
          <span :class="'dp__f--' + followState(item.next_follow_up_at)">{{ fmtDate(item.next_follow_up_at, true) || '—' }}</span>
        </template>
        <template #[`cell.created`]="{ item }">{{ fmtDate(item.created_at) }}</template>
        <template #empty><empty-state entity="deal" @create="shell.newDeal({ pipelineId })" /></template>
      </crm-table>

      <div v-else-if="view === 'list'" class="dp__scroll" @scroll="onListScroll">
        <div
          v-for="it in rows"
          :key="it.id"
          class="dp__row"
          tabindex="0"
          @click="open(it)"
          @keydown.enter="open(it)"
        >
          <span class="dp__row-bar" :style="{ background: stageColor(it.stage_id) }" />
          <div class="dp__row-main">
            <b>{{ it.title }}</b>
            <small>{{ [customer(it), stageTitle(it)].filter(Boolean).join(' · ') }}</small>
          </div>
          <crm-chip v-if="it.status !== 'open'" :value="it.status" small class="dp__hide-sm" />
          <span v-if="it.is_stale" class="dp__stale dp__hide-sm"><v-icon x-small>mdi-sleep</v-icon></span>
          <span class="dp__row-date dp__hide-sm" :class="'dp__f--' + followState(it.next_follow_up_at)">
            <v-icon x-small>mdi-calendar-clock-outline</v-icon>{{ fmtDate(it.next_follow_up_at) || $t('crmNoFollowUp') }}
          </span>
          <b class="dp__row-amt">
            <template v-if="it.financial_hidden || it.amount === null"><v-icon x-small>mdi-lock-outline</v-icon></template>
            <template v-else>{{ money(it.amount, true) }}</template>
          </b>
          <crm-avatar :user-id="it.owner_user_id" :size="26" />
        </div>
        <empty-state v-if="!loading && !rows.length" entity="deal" @create="shell.newDeal({ pipelineId })" />
        <div v-if="loading" class="dp__loading"><v-progress-circular indeterminate color="primary" :size="22" :width="2" /></div>
      </div>

      <deal-forecast
        v-else
        :pipeline-id="pipelineId"
        :params="params()"
        :show-money="showMoney"
        @open="open"
      />
    </div>
  </div>
</template>

<script>
import api, { crmError } from '../crmApi'
import {
  crm, can, toParams, pick, learnOwners, memberName, fmtDate, followState, money, dealCustomer, download, relDays, DEAL_STATUSES
} from '../crmStore'
import CrmToolbar, { emptyFilters } from '../components/CrmToolbar'
import CrmBoard from '../components/CrmBoard'
import CrmTable from '../components/CrmTable'
import BulkActions from '../components/BulkActions'
import EmptyState from '../components/EmptyState'
import DealForecast from './DealForecast'
import { CrmChip, CrmAvatar } from '../components/CrmBits'

const VIEW_KEY = 'crm-deals-view'
const FILTER_KEY = 'crm-deals-filters'
const PIPE_KEY = 'crm-deals-pipeline'

function readLS (k, fb) {
  try { const v = JSON.parse(localStorage.getItem(k)); return v === null ? fb : v } catch (e) { return fb }
}

export default {
  name: 'CrmDealsPage',
  components: { CrmToolbar, CrmBoard, CrmTable, BulkActions, EmptyState, DealForecast, CrmChip, CrmAvatar },
  inject: ['shell'],
  data () {
    const saved = readLS(PIPE_KEY, null)
    return {
      view: readLS(VIEW_KEY, 'board'),
      filters: { ...emptyFilters(), ...readLS(FILTER_KEY, {}) },
      pipelineId: crm.pipelines.some(p => p.id === saved) ? saved : (crm.pipelines[0] && crm.pipelines[0].id) || null,
      columns: [],
      rows: [],
      total: 0,
      page: 1,
      lastPage: 1,
      loading: false,
      exporting: false,
      tableOptions: { page: 1, itemsPerPage: 20, sortBy: [], sortDesc: [] },
      kpi: { openCount: 0, openAmount: 0, weighted: 0, wonCount: 0, wonAmount: 0, lostCount: 0 }
    }
  },
  computed: {
    pipelines () { return crm.pipelines },
    pipeline () { return crm.pipelines.find(p => p.id === this.pipelineId) || null },
    stages () { return this.pipeline ? this.pipeline.stages || [] : [] },
    statuses () { return DEAL_STATUSES },
    canCreate () { return can('deals.create') },
    canManage () { return can('pipelines.manage') },
    canExport () { return can('export.manage') },
    showMoney () { return can('deals.financial') },
    openColumns () { return this.columns.filter(c => c.kind === 'open') },
    winRate () {
      const t = this.kpi.wonCount + this.kpi.lostCount
      return t ? Math.round((this.kpi.wonCount / t) * 100) : 0
    },
    views () {
      return [
        { key: 'board', icon: 'mdi-view-column-outline', label: this.$t('crmViewPipeline') },
        { key: 'table', icon: 'mdi-table', label: this.$t('crmViewTable') },
        { key: 'list', icon: 'mdi-format-list-bulleted', label: this.$t('crmViewList') },
        { key: 'forecast', icon: 'mdi-chart-timeline-variant', label: this.$t('crmViewForecast') }
      ]
    },
    headers () {
      return [
        { text: this.$t('crmDealTitle'), value: 'title', fixed: true },
        { text: this.$t('crmAmount'), value: 'amount', align: 'end' },
        { text: this.$t('crmStage'), value: 'stage', sortable: false },
        { text: this.$t('crmStatus'), value: 'status' },
        { text: this.$t('crmPriority'), value: 'priority' },
        { text: this.$t('crmProbability'), value: 'probability' },
        { text: this.$t('crmOwner'), value: 'owner', sortable: false },
        { text: this.$t('crmExpectedClose'), value: 'close' },
        { text: this.$t('crmNextFollowUp'), value: 'next' },
        { text: this.$t('crmCreatedAt'), value: 'created', hidden: true }
      ]
    }
  },
  watch: {
    view (v) {
      try { localStorage.setItem(VIEW_KEY, JSON.stringify(v)) } catch (e) { /* ignore */ }
      this.reload()
    },
    pipelineId (v) {
      try { localStorage.setItem(PIPE_KEY, JSON.stringify(v)) } catch (e) { /* ignore */ }
      this.reload()
    }
  },
  created () {
    this.shell.$on('changed', this.onChanged)
    this.reload()
  },
  beforeDestroy () { this.shell.$off('changed', this.onChanged) },
  methods: {
    money,
    memberName,
    fmtDate,
    followState,
    num (v) { return Number(v || 0).toLocaleString(this.$i18n.locale === 'en' ? 'en-US' : 'fa-IR') },
    customer (d) { return dealCustomer(d) },
    stageTitle (d) {
      if (d.stage && d.stage.title) return d.stage.title
      const s = this.stages.find(x => x.id === d.stage_id)
      return s ? s.title : ''
    },
    stageColor (id) {
      const s = this.stages.find(x => x.id === id)
      return (s && s.color) || '#9aa4b2'
    },
    isLate (d) { return d.status === 'open' && relDays(d.expected_close_date) !== null && relDays(d.expected_close_date) < 0 },
    params () { return { ...toParams(this.filters, 'deal'), pipeline_id: this.pipelineId } },
    setFilters (f) {
      this.filters = f
      const { search, ...keep } = f
      try { localStorage.setItem(FILTER_KEY, JSON.stringify(keep)) } catch (e) { /* ignore */ }
      this.reload()
    },
    reload () {
      // the pipeline may have been deleted or created in the manager
      if (!crm.pipelines.some(p => p.id === this.pipelineId)) {
        const first = crm.pipelines[0]
        if (first && first.id !== this.pipelineId) { this.pipelineId = first.id; return }
      }
      if (!this.pipelineId) return
      if (this.view === 'board') return this.loadBoard()
      this.loadBoardSummary()
      if (this.view === 'table') return this.loadTable()
      if (this.view === 'list') { this.page = 1; this.rows = []; return this.loadList() }
    },
    mapColumns (res) {
      return ((res && res.columns) || []).map(c => ({
        key: c.stage.id,
        title: c.stage.title,
        color: c.stage.color,
        kind: c.stage.kind,
        stage: c.stage,
        count: c.count,
        amount: c.amount,
        weighted: c.weighted_amount,
        hasMore: !!c.has_more,
        items: (learnOwners(c.items), c.items || []),
        page: 1,
        loading: false
      }))
    },
    computeKpi (cols) {
      const k = { openCount: 0, openAmount: 0, weighted: 0, wonCount: 0, wonAmount: 0, lostCount: 0 }
      cols.forEach(c => {
        if (c.kind === 'open') { k.openCount += c.count || 0; k.openAmount += Number(c.amount) || 0; k.weighted += Number(c.weighted) || 0 }
        if (c.kind === 'won') { k.wonCount += c.count || 0; k.wonAmount += Number(c.amount) || 0 }
        if (c.kind === 'lost') k.lostCount += c.count || 0
      })
      this.kpi = k
    },
    async loadBoard () {
      this.loading = true
      try {
        const res = await api.kanban({ ...this.params(), per_column: 30 })
        this.columns = this.mapColumns(res)
        this.computeKpi(this.columns)
        this.total = this.columns.reduce((a, c) => a + (c.count || 0), 0)
      } catch (e) {
        this.shell.toast(crmError(e).message, { error: true })
      } finally {
        this.loading = false
      }
    },
    async loadBoardSummary () {
      try {
        const res = await api.kanban({ ...this.params(), per_column: 1 })
        const cols = this.mapColumns(res)
        this.columns = cols.map(c => ({ ...c, items: [] }))
        this.computeKpi(cols)
      } catch (e) { /* kpis optional */ }
    },
    async loadColumn (col) {
      if (!col.hasMore || col.loading) return
      col.loading = true
      try {
        const page = (col.page || 1) + 1
        const res = await api.kanbanColumn({ ...this.params(), stage_id: col.key, page, itemsPerPage: 30 })
        const items = pick(res).filter(i => !col.items.some(x => x.id === i.id))
        col.items = col.items.concat(items)
        col.page = res.current_page || page
        col.hasMore = res.last_page ? col.page < res.last_page : items.length > 0
      } catch (e) {
        this.shell.toast(crmError(e).message, { error: true })
      } finally {
        col.loading = false
      }
    },
    onOptions (o) {
      this.tableOptions = o
      this.loadTable()
    },
    async loadTable () {
      this.loading = true
      try {
        const o = this.tableOptions
        const sortMap = { close: 'expected_close_date', next: 'next_follow_up_at', created: 'created_at' }
        const res = await api.deals({
          ...this.params(),
          page: o.page,
          itemsPerPage: o.itemsPerPage,
          options: { page: o.page, itemsPerPage: o.itemsPerPage, sortDesc: o.sortDesc },
          sortBy: (o.sortBy || []).map(s => sortMap[s] || s)
        })
        this.rows = pick(res)
        this.total = res.total || this.rows.length
      } catch (e) {
        this.shell.toast(crmError(e).message, { error: true })
      } finally {
        this.loading = false
      }
    },
    async loadList () {
      this.loading = true
      try {
        const res = await api.deals({ ...this.params(), page: this.page, itemsPerPage: 40 })
        const items = pick(res)
        this.rows = this.page === 1 ? items : this.rows.concat(items)
        this.total = res.total || this.rows.length
        this.lastPage = res.last_page || 1
      } catch (e) {
        this.shell.toast(crmError(e).message, { error: true })
      } finally {
        this.loading = false
      }
    },
    onListScroll (e) {
      const el = e.target
      if (!this.loading && this.page < this.lastPage && el.scrollTop + el.clientHeight > el.scrollHeight - 200) {
        this.page++
        this.loadList()
      }
    },
    open (d, tab) { this.shell.openRecord('deal', d, tab) },
    onDrop ({ item, from, to, oldIndex }) { this.applyMove(item, from, to, oldIndex) },
    moveTo (item, from, to) {
      const oldIndex = from.items.indexOf(item)
      from.items = from.items.filter(i => i.id !== item.id)
      to.items.unshift(item)
      this.applyMove(item, from, to, oldIndex)
    },
    async applyMove (item, from, to, oldIndex) {
      const ok = await this.shell.moveDeal(item, to.stage, from.stage)
      if (!ok) {
        // optimistic update rolled back: card returns to its column
        to.items = to.items.filter(i => i.id !== item.id)
        from.items.splice(Math.max(0, oldIndex || 0), 0, item)
        return
      }
      const amt = Number(item.amount) || 0
      from.count = Math.max(0, (from.count || 1) - 1)
      to.count = (to.count || 0) + 1
      if (from.amount !== null && to.amount !== null) {
        from.amount = Math.max(0, (Number(from.amount) || 0) - amt)
        to.amount = (Number(to.amount) || 0) + amt
      }
      item.stage_id = to.key
      if (to.kind === 'won' || to.kind === 'lost') item.status = to.kind
      else if (item.status !== 'paused') item.status = 'open'
      this.computeKpi(this.columns)
    },
    onChanged ({ type }) {
      if (type === 'deal') this.reload()
    },
    async exportXlsx () {
      this.exporting = true
      try {
        const blob = await api.exportList({ ...this.params(), entity_type: 'deal', format: 'xlsx' })
        download(blob, 'crm-deals.xlsx')
      } catch (e) {
        this.shell.toast(crmError(e).message || this.$t('crmExportFailed'), { error: true })
      } finally {
        this.exporting = false
      }
    }
  }
}
</script>

<style scoped>
.dp { display: flex; flex-direction: column; gap: 12px; height: 100%; min-height: 0; }
.dp__icon { border-radius: 6px !important; }
.dp__btn { border-radius: 6px !important; letter-spacing: 0; height: 36px !important; }
.dp__kbd { margin-inline-start: 8px; font-size: .65rem; padding: 0 5px; border-radius: 4px; background: rgba(255, 255, 255, .25); box-shadow: none; }
.dp__pipe { display: inline-flex; align-items: center; gap: 8px; height: 38px; padding: 0 12px; border-radius: 6px; background: #fff; border: 1px solid rgba(15, 23, 42, .1); font-size: .85rem; font-weight: 700; max-width: 220px; }
.theme--dark .dp__pipe { background: #1e1e1e; border-color: rgba(255, 255, 255, .12); }
.dp__pipe-name { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dp__pipe-dot { width: 9px; height: 9px; border-radius: 50%; display: inline-block; flex-shrink: 0; }
.dp__kpis { display: grid; grid-template-columns: repeat(4, minmax(0, 170px)) 1fr; gap: 8px; flex-shrink: 0; }
.dp__kpi { display: flex; flex-direction: column; padding: 10px 14px; border-radius: 6px; background: #fff; border: 1px solid rgba(15, 23, 42, .08); }
.theme--dark .dp__kpi { background: #1e1e1e; border-color: rgba(255, 255, 255, .08); }
.dp__kpi-l { font-size: .72rem; opacity: .6; }
.dp__kpi b { font-size: 1.15rem; font-weight: 800; line-height: 1.5; }
.dp__kpi small { font-size: .72rem; opacity: .7; }
.dp__kpi--won b { color: #0f9d76; }
.dp__kpi--lost b { color: #c0392b; }
.dp__funnel { display: flex; gap: 2px; align-items: stretch; border-radius: 6px; overflow: hidden; min-height: 56px; }
.dp__funnel-seg { display: flex; align-items: flex-end; padding: 6px 8px; color: #fff; font-size: .7rem; font-weight: 600; min-width: 0; opacity: .9; transition: flex .3s; }
.dp__funnel-seg span { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dp__body { flex: 1; min-height: 0; }
.dp__scroll { height: 100%; overflow-y: auto; overflow-x: hidden; padding-bottom: 20px; }
.dp__row { position: relative; display: flex; align-items: center; gap: 12px; padding: 10px 14px 10px 14px; background: #fff; border: 1px solid rgba(15, 23, 42, .07); border-radius: 6px; margin-bottom: 6px; cursor: pointer; overflow: hidden; outline: none; transition: box-shadow .15s; }
.dp__row:hover, .dp__row:focus-visible { box-shadow: 0 3px 10px rgba(15, 23, 42, .08); }
.theme--dark .dp__row { background: #1e1e1e; border-color: rgba(255, 255, 255, .08); }
.dp__row-bar { position: absolute; inset-inline-start: 0; top: 0; bottom: 0; width: 4px; }
.dp__row-main { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.dp__row-main b { font-size: .86rem; }
.dp__row-main small { font-size: .74rem; opacity: .6; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dp__row-date { display: inline-flex; align-items: center; gap: 4px; font-size: .74rem; opacity: .75; min-width: 120px; }
.dp__row-amt { min-width: 90px; text-align: end; font-size: .88rem; }
.dp__stale { width: 24px; height: 24px; border-radius: 6px; background: rgba(127, 127, 127, .14); display: inline-flex; align-items: center; justify-content: center; }
.dp__cell-title { display: flex; flex-direction: column; line-height: 1.35; max-width: 280px; }
.dp__cell-title b { overflow: hidden; text-overflow: ellipsis; }
.dp__cell-title small { opacity: .6; font-size: .72rem; overflow: hidden; text-overflow: ellipsis; }
.dp__stage { display: inline-flex; align-items: center; gap: 6px; font-weight: 600; font-size: .78rem; }
.dp__stage::before { content: ''; width: 8px; height: 8px; border-radius: 50%; background: var(--sc); }
.dp__prob { display: inline-block; width: 48px; height: 4px; border-radius: 4px; background: rgba(127, 127, 127, .15); overflow: hidden; margin-inline-end: 6px; vertical-align: middle; }
.dp__prob i { display: block; height: 100%; background: var(--v-primary-base); }
.dp__owner { display: inline-flex; align-items: center; gap: 6px; }
.dp__muted { opacity: .5; }
.dp__late { color: #c0392b; font-weight: 600; }
.dp__f--overdue { color: #c0392b; font-weight: 600; opacity: 1; }
.dp__f--today { color: var(--v-primary-base); font-weight: 600; opacity: 1; }
.dp__loading { display: flex; justify-content: center; padding: 16px; }
@media (max-width: 1100px) {
  .dp__kpis { grid-template-columns: repeat(4, minmax(0, 1fr)); }
  .dp__funnel { grid-column: 1 / -1; min-height: 34px; }
}
@media (max-width: 799px) {
  .dp__kpis { display: flex; overflow-x: auto; }
  .dp__kpi { flex: 0 0 140px; }
  .dp__funnel { display: none; }
  .dp__hide-sm { display: none !important; }
  .dp__pipe { max-width: 160px; }
}
</style>
